import api from './api';

export interface MessageAttachment {
  id: string;
  name: string;
  size: number;
  type: 'document' | 'image' | 'audio' | string;
  url: string;
  preview?: string | null;
}

export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  created_at: Date;
  session_id: string;
  tokens_used?: number;
  provider?: string;
  role?: 'user' | 'assistant' | 'system';
  isStreaming?: boolean;
  attachments?: MessageAttachment[];
}

export interface ChatSession {
  id: string;
  title: string;
  custom_title?: string;
  display_title?: string;
  is_favorite?: boolean;
  messages?: Message[];
  created_at: Date;
  updated_at: Date;
  updatedAt: Date;
  createdAt: Date;
  messageCount: number;
  user_id: string;
}

export type ChatProvider = 'openai' | 'claude' | 'deepseek';

export interface ChatRequest {
  message: string;
  session_id: string;
  attachments?: string[]; // IDs dos arquivos anexados
  // provider removido - agora configurado via variável de ambiente no backend
}

export interface ChatResponse {
  message: string;
  session_id: string;
  tokens_used?: number;
  provider: string;
}

export interface ChatStreamResponse {
  content?: string;
  error?: string;
  done?: boolean;
  tokens_used?: number;
  provider?: string;
}

// Helper function to transform date strings to Date objects
const transformDates = (session: any): ChatSession => {
  // console.log('Transforming session:', session);
  const transformed = {
    ...session,
    id: session.id,
    created_at: new Date(session.created_at),
    updated_at: new Date(session.updated_at),
    updatedAt: new Date(session.updated_at),
    createdAt: new Date(session.created_at),
    title: session.title,
    custom_title: session.custom_title,
    display_title: session.display_title || session.custom_title || session.title || 'Nova conversa',
    is_favorite: session.is_favorite || false,
    user_id: session.user_id,
    messageCount: session.messages?.length || 0,
    messages: session.messages?.map((msg: any) => {
      // console.log('Transforming message:', msg);
      return {
        id: msg.id,
        content: msg.content,
        isUser: msg.role === 'user',
        created_at: new Date(msg.created_at),
        createdAt: new Date(msg.created_at),
        session_id: session.id,
        tokens_used: msg.tokens_used,
        provider: msg.provider,
        role: msg.role,
        attachments: Array.isArray(msg.attachments) ? msg.attachments : []
      };
    })
  };
  // console.log('Transformed session:', transformed);
  return transformed;
};

/**
 * Get all chat sessions for the user
 */
export const getChatSessions = async (skip: number = 0, limit: number = 25): Promise<ChatSession[]> => {
  try {
    const response = await api.get('/chat/', {
      params: { skip, limit }
    });
    return response.data.map(transformDates);
  } catch (error) {
    console.error('Error getting chat sessions:', error);
    throw error;
  }
};

/**
 * Get a specific chat session with its messages
 */
export const getChatSession = async (sessionId: string): Promise<ChatSession> => {
  try {
    // console.log('Fetching session:', sessionId);
    const response = await api.get(`/chat/${sessionId}`);
    // console.log('API response:', response.data);
    const session = response.data;
    
    // Sort messages by created_at
    if (session.messages) {
      // console.log('Messages before sorting:', session.messages);
      session.messages.sort((a: any, b: any) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    }
    
    return transformDates(session);
  } catch (error) {
    console.error('Error getting chat session:', error);
    throw error;
  }
};

/**
 * Create a new chat session
 */
export const createChatSession = async (title = 'Nova Conversa'): Promise<ChatSession> => {
  try {
    const response = await api.post('/chat', { title });
    return transformDates(response.data);
  } catch (error) {
    console.error('Error creating chat session:', error);
    throw error;
  }
};

/**
 * Update a chat session
 */
export const updateChatSession = async (
  sessionId: string, 
  data: { title?: string }
): Promise<ChatSession> => {
  try {
    const response = await api.put(`/chat/${sessionId}`, data);
    return transformDates(response.data);
  } catch (error) {
    console.error('Error updating chat session:', error);
    throw error;
  }
};

/**
 * Rename a chat session with a custom title
 */
export const renameChatSession = async (
  sessionId: string, 
  customTitle: string
): Promise<{ id: string; custom_title: string; display_title: string; message: string }> => {
  try {
    const response = await api.post(`/chat/${sessionId}/rename`, { custom_title: customTitle });
    return response.data;
  } catch (error) {
    console.error('Error renaming chat session:', error);
    throw error;
  }
};

/**
 * Toggle favorite status of a chat session
 */
export const toggleFavoriteSession = async (
  sessionId: string, 
  isFavorite: boolean
): Promise<{ id: string; is_favorite: boolean; message: string }> => {
  try {
    const response = await api.post(`/chat/${sessionId}/favorite`, { is_favorite: isFavorite });
    return response.data;
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};

/**
 * Get favorite chat sessions
 */
export const getFavoriteSessions = async (skip: number = 0, limit: number = 50): Promise<ChatSession[]> => {
  try {
    const response = await api.get('/chat/favorites/list', {
      params: { skip, limit }
    });
    return response.data.map(transformDates);
  } catch (error) {
    console.error('Error getting favorite sessions:', error);
    throw error;
  }
};

/**
 * Send a message to a chat session
 */
export const sendMessage = async (data: ChatRequest): Promise<ChatResponse> => {
  try {
    const response = await api.post('/chat/message', data);
    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Stream chat responses
 */
export const streamChat = async (
  data: ChatRequest,
  onChunk: (chunk: string) => void
): Promise<void> => {
  try {
    // console.log('Iniciando streamChat com dados:', data);
    
    if (!data.session_id) {
      console.error('Session ID is missing');
      throw new Error('Session ID is required');
    }
    
    const response = await fetch(`${api.defaults.baseURL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(data)
    });

    // console.log('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Stream API error:', response.status, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No reader available');
    }

    // console.log('Stream reader obtained, starting to process chunks');

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // console.log('Stream completed');
        break;
      }

      const chunk = new TextDecoder().decode(value);
      // console.log('Received chunk:', chunk.substring(0, 50) + (chunk.length > 50 ? '...' : ''));
      
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.error) {
              console.error('Stream error received:', data.error);
              throw new Error(data.error);
            }
            if (data.content) {
              onChunk(data.content);
            }
            if (data.done) {
              // console.log('Stream signaled completion');
              return;
            }
          } catch (parseError) {
            console.error('Error parsing streaming response:', parseError, 'Line:', line);
            // Re-throw the error if it's not a JSON parsing error
            if (parseError instanceof Error && parseError.message !== 'Unexpected token') {
              throw parseError;
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('Error streaming chat:', error);
    throw error;
  }
};

/**
 * Delete a chat session
 */
export const deleteChatSession = async (sessionId: string): Promise<void> => {
  try {
    await api.delete(`/chat/${sessionId}`);
  } catch (error) {
    console.error('Error deleting chat session:', error);
    throw error;
  }
};

/**
 * Upload a file attachment for chat
 */
export interface ChatAttachmentResponse {
  file_id: string;
  filename: string;
  size: number;
  type: string;
  transcription?: string;
  preview_url?: string;
}

export const uploadChatAttachment = async (file: File): Promise<ChatAttachmentResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Usar fetch diretamente ao invés de axios para evitar problemas com FormData
    const token = localStorage.getItem('token');
    const baseURL = import.meta.env.VITE_API_URL || '/api/v1';
    
    const response = await fetch(`${baseURL}/chat/attachments/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // NÃO incluir Content-Type - o navegador adiciona automaticamente com boundary
      },
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error uploading chat attachment:', error);
    throw error;
  }
};